import React, { useMemo } from "react";
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  type SensorDescriptor,
  type SensorOptions,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import PaginationComponent from "../../Atomos/Pagination/PaginationComponent";
import { IPagination2, ISorting } from "../../../interfaces/Components/ITable";
import DraggableHeaderCell from "./DraggableHeaderCell";
import GroupedHeaderParentCell from "./GroupedHeaderParentCell";
import { IGroupByConfig, IHeader } from "./FilteredTable.types";
import {
  buildGroupedRows,
  buildHeaderLayout,
  buildSingleGroupRows,
  safeCellValue,
} from "./FilteredTable.utils";
import TableHorizontalScrollTop from "./TableHorizontalScrollTop";

interface TableSectionProps {
  topPagination: boolean;
  bottomPagination: boolean;
  totalCount: number;
  pagination: IPagination2;
  onPageChange: (pageNumber: number, pageSize: number) => void;
  sensors: SensorDescriptor<SensorOptions>[];
  onDragEnd: (event: DragEndEvent) => void;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  topContent?: React.ReactNode;
  showTopHorizontalScrollBar?: boolean;
  hasHorizontalScroll: boolean;
  horizontalViewportWidth: number;
  horizontalScrollThumbWidth: number;
  horizontalScrollThumbLeft: number;
  horizontalMaxScrollLeft: number;
  onHorizontalScrollLeftChange: (scrollLeft: number) => void;
  headerScrollRef: React.RefObject<HTMLDivElement>;
  bodyScrollRef: React.RefObject<HTMLDivElement>;
  onBodyHorizontalScroll: () => void;
  hasAllWidths: boolean;
  colgroup: React.ReactNode;
  visibleHeaders: IHeader[];
  sorting: ISorting;
  onSortingClick: (headerId: string) => void;
  getColumnWidth: (header: IHeader) => number | undefined;
  onColumnResize: (columnId: string, newWidth: number) => void;
  formattedTableData: any[];
  emptyStateMessage?: string | React.ReactNode;
  /**
   * When set (stacked sticky with FilteredTableCore quick-filter bar), thead sticks below that bar instead of overlapping it.
   */
  stickyHeaderOffsetPx?: number;
  /** Optional single grouping: renders a subtitle row per distinct `groupBy.field` value. */
  groupBy?: IGroupByConfig;
  /**
   * When set (non-null), collapses the grouped display to a single group labeled with this
   * value, containing every row in `formattedTableData` — see `IGroupByConfig.filterName`.
   */
  groupByOverrideLabel?: React.ReactNode | null;
  containerclassName?: string;
  /** When true, table body scrolls vertically inside a flex parent (e.g. modal). */
  embeddedInScrollContainer?: boolean;
}

export default function TableSection({
  topPagination,
  bottomPagination,
  totalCount,
  pagination,
  onPageChange,
  sensors,
  onDragEnd,
  tableContainerRef,
  topContent,
  showTopHorizontalScrollBar = false,
  hasHorizontalScroll,
  horizontalViewportWidth,
  horizontalScrollThumbWidth,
  horizontalScrollThumbLeft,
  horizontalMaxScrollLeft,
  onHorizontalScrollLeftChange,
  headerScrollRef,
  bodyScrollRef,
  onBodyHorizontalScroll,
  hasAllWidths,
  colgroup,
  visibleHeaders,
  sorting,
  onSortingClick,
  getColumnWidth,
  onColumnResize,
  formattedTableData,
  emptyStateMessage,
  stickyHeaderOffsetPx,
  groupBy,
  groupByOverrideLabel,
  containerclassName,
  embeddedInScrollContainer = false,
}: TableSectionProps) {
  const headerStickyStacked =
    stickyHeaderOffsetPx != null && stickyHeaderOffsetPx > 0;

  const { useGroupedLayout, segments } = useMemo(
    () => buildHeaderLayout(visibleHeaders),
    [visibleHeaders],
  );

  const headerColumnIndexById = useMemo(() => {
    const map = new Map<string, number>();
    visibleHeaders.forEach((header, index) => {
      map.set(header.id, index);
    });
    return map;
  }, [visibleHeaders]);

  const groupDragIdsByColumnId = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const segment of segments) {
      if (segment.kind !== "group") {
        continue;
      }
      const ids = segment.columns.map((column) => column.id);
      for (const id of ids) {
        map.set(id, ids);
      }
    }
    return map;
  }, [segments]);

  const getGroupDragIds = (columnId: string) =>
    groupDragIdsByColumnId.get(columnId);

  const groupedEntries = useMemo(() => {
    if (!groupBy) {
      return null;
    }
    if (groupByOverrideLabel != null) {
      return buildSingleGroupRows(formattedTableData ?? [], groupByOverrideLabel);
    }
    return buildGroupedRows(formattedTableData ?? [], groupBy);
  }, [formattedTableData, groupBy, groupByOverrideLabel]);

  const renderDataRow = (row: any, rowIndex: number, key: React.Key) => (
    <tr key={key} className="even:bg-gray-100 text-gray-font text-xs">
      {visibleHeaders.map((h, colIndex) => {
        const value = row?.[h.id];
        const w = getColumnWidth(h);
        const isFirst = colIndex === 0;
        const isLast = colIndex === visibleHeaders.length - 1;
        const allowCellOverflow = h.id === "actions";
        const horizontalPaddingClass =
          isFirst && isLast
            ? "px-4"
            : isFirst
              ? "pl-4 pr-1"
              : isLast
                ? "pl-1 pr-4"
                : "px-1";
        return (
          <td
            key={`${rowIndex}-${h.id}`}
            className={`${horizontalPaddingClass} py-2 ${
              hasAllWidths && !allowCellOverflow ? "overflow-hidden" : ""
            } ${allowCellOverflow ? "overflow-visible" : ""}`}
            style={
              hasAllWidths
                ? {
                    maxWidth: w ? `${w}px` : undefined,
                  }
                : {
                    width: w ? `${w}px` : undefined,
                    minWidth: w ? `${w}px` : undefined,
                  }
            }
          >
            <div
              style={
                hasAllWidths
                  ? {
                      overflow: allowCellOverflow ? "visible" : "hidden",
                      width: "100%",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }
                  : undefined
              }
            >
              {h.cell
                ? h.cell({ row, rowIndex, value, pagination })
                : safeCellValue(value)}
            </div>
          </td>
        );
      })}
    </tr>
  );

  const renderHeaderCells = () =>
    visibleHeaders.map((h, colIndex) => (
      <DraggableHeaderCell
        key={h.id}
        header={h}
        sorting={sorting}
        onSortingClick={onSortingClick}
        colWidthPx={getColumnWidth(h)}
        onResize={onColumnResize}
        useFixedLayout={hasAllWidths}
        isFirstColumn={colIndex === 0}
        isLastColumn={colIndex === visibleHeaders.length - 1}
        groupDragIds={getGroupDragIds(h.id)}
        disableSticky={embeddedInScrollContainer}
      />
    ));

  const renderGroupedHeaderRows = () => (
    <>
      <tr>
        {segments.map((segment) => {
          if (segment.kind === "leaf") {
            const colIndex = headerColumnIndexById.get(segment.header.id) ?? 0;
            return (
              <DraggableHeaderCell
                key={segment.header.id}
                header={segment.header}
                sorting={sorting}
                onSortingClick={onSortingClick}
                colWidthPx={getColumnWidth(segment.header)}
                onResize={onColumnResize}
                useFixedLayout={hasAllWidths}
                isFirstColumn={colIndex === 0}
                isLastColumn={colIndex === visibleHeaders.length - 1}
                rowSpan={2}
                groupDragIds={getGroupDragIds(segment.header.id)}
                disableSticky={embeddedInScrollContainer}
              />
            );
          }

          const firstColIndex =
            headerColumnIndexById.get(segment.columns[0].id) ?? 0;
          const lastColIndex =
            headerColumnIndexById.get(
              segment.columns[segment.columns.length - 1].id,
            ) ?? visibleHeaders.length - 1;

          return (
            <GroupedHeaderParentCell
              key={`group-${segment.columns[0].id}`}
              label={segment.label}
              labelTooltip={segment.labelTooltip}
              colSpan={segment.columns.length}
              isFirstColumn={firstColIndex === 0}
              isLastColumn={lastColIndex === visibleHeaders.length - 1}
            />
          );
        })}
      </tr>
      <tr>
        {segments.flatMap((segment) => {
          if (segment.kind === "leaf") {
            return [];
          }

          return segment.columns.map((header, columnIndex) => {
            const colIndex = headerColumnIndexById.get(header.id) ?? 0;
            const isLastInGroup = columnIndex === segment.columns.length - 1;
            return (
              <DraggableHeaderCell
                key={header.id}
                header={header}
                sorting={sorting}
                onSortingClick={onSortingClick}
                colWidthPx={getColumnWidth(header)}
                onResize={onColumnResize}
                useFixedLayout={hasAllWidths}
                isFirstColumn={colIndex === 0}
                isLastColumn={colIndex === visibleHeaders.length - 1}
                isSubHeader
                isLastInGroup={isLastInGroup}
                groupDragIds={getGroupDragIds(header.id)}
                disableSticky={embeddedInScrollContainer}
              />
            );
          });
        })}
      </tr>
    </>
  );

  return (
    <div
      className={`bg-white px-6 min-w-0${
        embeddedInScrollContainer ? " flex min-h-0 flex-1 flex-col" : ""
      } ${containerclassName ?? ""}`}
    >
      {topPagination && (
        <PaginationComponent
          totalCount={totalCount}
          currentPage={pagination.pageIndex + 1}
          recordsPerPage={pagination.pageSize}
          onPageChange={onPageChange}
        />
      )}

      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        sensors={sensors}
      >
        <div
          ref={tableContainerRef}
          className={`table-responsive tabledata w-full min-w-0${
            embeddedInScrollContainer ? " flex min-h-0 flex-1 flex-col" : ""
          }`}
          tabIndex={0}
        >
          {topContent}
          {showTopHorizontalScrollBar && (
            <TableHorizontalScrollTop
              visible={hasHorizontalScroll}
              trackWidth={horizontalViewportWidth}
              thumbWidth={horizontalScrollThumbWidth}
              thumbLeft={horizontalScrollThumbLeft}
              maxScrollLeft={horizontalMaxScrollLeft}
              onScrollLeftChange={onHorizontalScrollLeftChange}
            />
          )}
          <div
            className={
              embeddedInScrollContainer
                ? `z-20 shrink-0 overflow-x-hidden ${
                    showTopHorizontalScrollBar && hasHorizontalScroll
                      ? ""
                      : "rounded-t-lg"
                  }`
                : headerStickyStacked
                  ? `sticky z-30 shrink-0 overflow-x-hidden bg-white shadow-sm ${
                      showTopHorizontalScrollBar && hasHorizontalScroll
                        ? ""
                        : "rounded-t-lg"
                    }`
                  : `sticky top-0 z-20 shrink-0 overflow-x-hidden ${
                      showTopHorizontalScrollBar && hasHorizontalScroll
                        ? ""
                        : "rounded-t-lg"
                    }`
            }
            style={
              headerStickyStacked && !embeddedInScrollContainer
                ? { top: stickyHeaderOffsetPx }
                : undefined
            }
          >
            <div ref={headerScrollRef} className="w-full overflow-x-hidden">
              <table
                className="table min-w-full w-max"
                style={{ tableLayout: hasAllWidths ? "fixed" : "auto" }}
              >
                {colgroup}
                <thead>
                  <SortableContext
                    items={visibleHeaders.map((h) => h.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    {useGroupedLayout ? (
                      renderGroupedHeaderRows()
                    ) : (
                      <tr>{renderHeaderCells()}</tr>
                    )}
                  </SortableContext>
                </thead>
              </table>
            </div>
          </div>

          <div
            ref={bodyScrollRef}
            className={`w-full min-w-0 overflow-x-auto${
              embeddedInScrollContainer
                ? " min-h-0 flex-1 overflow-y-auto"
                : ""
            }`}
            onScroll={onBodyHorizontalScroll}
          >
            <table
              className="table min-w-full w-max"
              style={{ tableLayout: hasAllWidths ? "fixed" : "auto" }}
            >
              {colgroup}
              <tbody>
                {groupedEntries ? (
                  groupedEntries.length ? (
                    groupedEntries.map((entry) =>
                      entry.kind === "header" ? (
                        <tr key={entry.key}>
                          <td
                            colSpan={visibleHeaders.length || 1}
                            className="bg-gray-font px-4 py-2 text-left text-sm font-bold text-white"
                          >
                            {entry.label}
                          </td>
                        </tr>
                      ) : (
                        renderDataRow(entry.row, entry.rowIndex, entry.key)
                      ),
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={visibleHeaders.length || 1}
                        className="p-6 text-center"
                      >
                        {emptyStateMessage ?? "No data available."}
                      </td>
                    </tr>
                  )
                ) : formattedTableData?.length ? (
                  formattedTableData.map((row, rowIndex) =>
                    renderDataRow(row, rowIndex, row?.id ?? rowIndex),
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={visibleHeaders.length || 1}
                      className="p-6 text-center"
                    >
                      {emptyStateMessage ?? "No data available."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </DndContext>

      {bottomPagination && (
        <PaginationComponent
          totalCount={totalCount}
          currentPage={pagination.pageIndex + 1}
          recordsPerPage={pagination.pageSize}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
